import { OrderDeliveryHistory } from "../../../pages/order/deliveryHistory/OrderDeliveryHistory";
import { useOrderDeliveryHistory } from "../../../hooks/api/v2/order/useOrderDeliveryHistory";
import {
  OrderGetResponseType,
  OrderPaymentType,
} from "@sellar-io/api-v2/types";
import React, { ReactNode } from "react";
import { OrderContentConnectionsType } from "../../../pages/order/OrderContent/OrderContent";
import PaymentStatusIndicator from "../../../pages/order/paymentStatusIndicator/PaymentStatusIndicator";
import { CardContent, Icon, SmallButton } from "@sellar-io/components";
import { PaymentTermsIndicator } from "../../paymentTypeIndicator/PaymentTermsIndicator";
import { getOrderStage } from ".././helpers";
import { OrderPaymentStatus } from "../OrderPaymentStatus/OrderPaymentStatus";
import { OrderStatusBar } from "../OrderStatusBar";
import { OrderStatusHeader } from "../OrderStatusHeader";
import { OrderStatusText } from "../OrderStatusText";
import { OrderSummaryStatusPaymentMessage } from "./OrderSummaryStatusPaymentMessage/OrderSummaryStatusPaymentMessage";
import { SellarCreditPaymentTypeIndicator } from "../../paymentTypeIndicator/SellarCreditPaymentTypeIndicator";
import { SellarCreditOrderSummaryStatusPaymentMessage } from "./OrderSummaryStatusPaymentMessage/SellarCreditOrderSummaryStatusPaymentMessage";
import { SellarCreditOrderPaymentStatus } from "../OrderPaymentStatus/SellarCreditOrderPaymentStatus";
import { SellarCreditPaymentStatusIndicator } from "../../../pages/order/paymentStatusIndicator/SellarCreditPaymentStatusIndicator";
import { useSellarUser } from "../../../hooks";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useOrderDocumentList } from "../../../hooks/api/v2/orderDocument/useOrderDocumentList";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { ImplicitProformaPaymentStatus } from "./ImplicitProformaPaymentStatus";
import { ImplicitProformaInvoiceStatus } from "./ImplicitProformaInvoiceStatus";

export const OrderSummaryStatusCardContent = ({
  order,
  onVatInvoiceClick,
  connection,
}: {
  order: OrderGetResponseType;
  onVatInvoiceClick?: () => void;
  connection?: OrderContentConnectionsType;
}): React.ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const deliveryHistory = useOrderDeliveryHistory(
    order.id,
    order.fulfillmentType === "sellarDistribution",
  );
  const showCurrentDeliveryDates =
    deliveryHistory.data?.affected &&
    !order.cancelledAt &&
    !order.voidedAt &&
    !order.deliveredAt;
  const invoiceCreationStage = order.Supplier.invoiceCreationStage;
  const stage = () => getOrderStage(order);
  const requestedPaymentTermsPending =
    connection?.PaymentTermsRequest?.status === "pending"
      ? connection.PaymentTermsRequest
      : null;
  const showSellarCreditFeatures = order.paymentType === "sellarCredit";
  const { user } = useSellarUser();
  const isImplicitProformaEnabled =
    useRemoteFeatureFlags().isActive("implicitProforma");
  const isImplicitProforma =
    isImplicitProformaEnabled &&
    !!business?.Retailer &&
    order.paymentType === "manual" &&
    !order.PaymentLink;
  const isDirector = Boolean(user?.legalEntityAdminList.length);
  const isUserSellarAdmin = Boolean(user?.type === "admin");
  const canAccessTab = isDirector || isUserSellarAdmin;
  const userType = business?.Retailer ? "buyer" : "supplier";

  const orderDocumentQuery = useOrderDocumentList(
    {
      limit: 50,
      query: [
        userType === "supplier" && {
          type: "SupplierId",
          modifier: "EQUALS",
          value: business?.Supplier?.id,
        },
        userType === "buyer" && {
          type: "RetailerId",
          modifier: "EQUALS",
          value: business?.Retailer?.id,
        },
        { type: "OrderId", modifier: "EQUALS", value: order.id },
      ].filter(Boolean),
    },
    {
      enabled: showSellarCreditFeatures,
    },
  );

  const documents = orderDocumentQuery.data?.pages[0] ?? [];
  const sellarCreditOrderInvoice = documents.find(
    (document) => document.type === "invoice",
  );
  const sellarCreditOrderCreditNotes = documents.filter(
    (document) => document.type === "credit-note",
  );

  const sellarCreditOrderInvoicePaidAt =
    sellarCreditOrderInvoice?.paidAt ?? null;
  const deferredPaymentId = order.Payment?.deferredPaymentId ?? null;

  const showImplicitProformaPaymentStatus =
    !order.voidedAt &&
    order.paymentStatus !== "void" &&
    (!order.cancelledAt || order.paymentStatus === "refunded");
  const showImplicitProformaInvoiceStatus =
    !(showSellarCreditFeatures && !sellarCreditOrderInvoice?.issuedAt) &&
    !order.cancelledAt;

  if (isImplicitProformaEnabled) {
    return (
      <div className="flex flex-col gap-4 divide-y divide-gray-200">
        <CardContent className="p-4">
          <div className="flex h-full flex-col justify-between gap-4">
            <OrderStatusHeader
              stage={stage()}
              fulfilment={deliveryHistory.data?.fulfilment}
              fulfillmentType={order.fulfillmentType}
            />
            <OrderStatusBar stage={stage()} />
            {!showCurrentDeliveryDates && (
              <OrderStatusText
                cancelledAt={order.cancelledAt}
                voidedAt={order.voidedAt}
                dispatchPlannedAt={order.dispatchPlannedAt}
                deliveryPlannedAt={order.deliveryPlannedAt}
                deliveredAt={order.deliveredAt}
                stage={stage()}
                createdAt={order.createdAt}
                supplierName={order.Supplier.Business.name}
                buyerName={order.Retailer.Business.name}
                cancelledBy={order.cancelledBy}
                cancellationReason={order.cancellationReason}
                fulfillmentType={order.fulfillmentType}
                acceptedAt={order.acceptedAt}
              />
            )}
            <OrderDeliveryHistory
              key={order.id}
              order={order}
              query={deliveryHistory}
            />
          </div>
        </CardContent>
        {(showImplicitProformaPaymentStatus ||
          showImplicitProformaInvoiceStatus) && (
          <div className="flex flex-col gap-4 p-4 pt-0 empty:hidden">
            <ImplicitProformaPaymentStatus
              order={order}
              connection={connection}
            />
            <ImplicitProformaInvoiceStatus
              order={order}
              onVatInvoiceClick={onVatInvoiceClick}
              connection={connection}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid gap-2 md:grid-cols-2">
      <CardContent
        className={
          "min-h-[150px] border-b border-solid border-gray-200 p-4 md:col-span-1 md:border-r md:border-b-0"
        }
      >
        <div className="flex h-full flex-col justify-between">
          <OrderStatusHeader
            stage={stage()}
            fulfilment={deliveryHistory.data?.fulfilment}
            fulfillmentType={order.fulfillmentType}
          />
          <OrderStatusBar stage={stage()} />
          {!showCurrentDeliveryDates && (
            <OrderStatusText
              cancelledAt={order.cancelledAt}
              voidedAt={order.voidedAt}
              dispatchPlannedAt={order.dispatchPlannedAt}
              deliveryPlannedAt={order.deliveryPlannedAt}
              deliveredAt={order.deliveredAt}
              stage={stage()}
              createdAt={order.createdAt}
              supplierName={order.Supplier.Business.name}
              buyerName={order.Retailer.Business.name}
              cancelledBy={order.cancelledBy}
              cancellationReason={order.cancellationReason}
              fulfillmentType={order.fulfillmentType}
              acceptedAt={order.acceptedAt}
            />
          )}
          {onVatInvoiceClick && (
            <div>
              <SmallButton>
                <Icon icon={"OPEN_IN_NEW"} />
                VAT INVOICE
              </SmallButton>
            </div>
          )}
          <OrderDeliveryHistory
            key={order.id}
            order={order}
            query={deliveryHistory}
          />
        </div>
      </CardContent>
      {showSellarCreditFeatures && (
        <PaymentStatusContainer
          paymentStatus={
            <SellarCreditOrderPaymentStatus
              invoicePaidAt={sellarCreditOrderInvoicePaidAt}
              orderStage={stage()}
              userType={userType}
              orderId={order.id}
              initiator={order.initiator}
            />
          }
          paymentTermsIndicator={
            <SellarCreditPaymentTypeIndicator
              canAccessTab={canAccessTab}
              orderStage={stage()}
              userType={userType}
              invoiceId={sellarCreditOrderInvoice?.id}
              payoutId={sellarCreditOrderInvoice?.PayoutId}
            />
          }
          paymentStatusIndicator={
            <SellarCreditPaymentStatusIndicator
              invoicePaidAt={sellarCreditOrderInvoicePaidAt}
              isDirector={isDirector}
              userType={userType}
            />
          }
          paymentMessage={
            <SellarCreditOrderSummaryStatusPaymentMessage
              orderStage={stage()}
              isDirector={isDirector}
              userType={userType}
              invoicePaidAt={sellarCreditOrderInvoicePaidAt}
              nbCreditNotes={sellarCreditOrderCreditNotes.length}
              deferredPaymentId={deferredPaymentId}
              invoiceId={sellarCreditOrderInvoice?.id}
            />
          }
          price={order.price}
          onVatInvoiceClick={onVatInvoiceClick}
        />
      )}
      {!showSellarCreditFeatures && (
        <PaymentStatusContainer
          paymentStatus={
            <OrderPaymentStatus
              orderStage={stage()}
              order={order}
              isImplicitProforma={isImplicitProforma}
            />
          }
          paymentTermsIndicator={
            isImplicitProforma ? null : (
              <PaymentTermsIndicator
                connection={connection}
                invoiceCreationStage={invoiceCreationStage}
                paymentTermsRequest={requestedPaymentTermsPending}
                paymentType={order.paymentType}
              />
            )
          }
          paymentStatusIndicator={
            isImplicitProforma ? null : (
              <PaymentStatusIndicator
                paymentStatus={order.paymentStatus}
                voidedAt={order.voidedAt}
                paidAt={order.paidAt}
                retailerPaidAt={order.retailerPaidAt}
                paymentType={order.paymentType as OrderPaymentType}
                paymentDue={order.paymentDue}
                cancelledAt={order.cancelledAt}
                invoiceIssuedAt={order.invoiceIssuedAt}
                invoicesEnabled={order.Supplier.invoicesEnabled}
                showOverdueStatus={true}
              />
            )
          }
          paymentMessage={
            isImplicitProforma ? (
              <>
                {order.Supplier.Business.name} will issue the VAT invoice for
                this order outside of Sellar. They may request payment before
                your order is dispatched unless you already have agreed credit
                terms with them.
              </>
            ) : (
              <OrderSummaryStatusPaymentMessage
                paidAt={order.paidAt}
                paymentDue={order.paymentDue}
                paymentStatus={order.paymentStatus}
                orderStage={stage()}
                cancelledAt={order.cancelledAt}
                invoiceIssuedAt={order.invoiceIssuedAt}
                requestedAt={order.createdAt}
                invoiceCreationStage={order.Supplier.invoiceCreationStage}
                paymentTerms={connection?.paymentTerms}
                paymentType={order.paymentType}
                orderPrice={order.price}
              />
            )
          }
          price={order.price}
          onVatInvoiceClick={onVatInvoiceClick}
        />
      )}
    </div>
  );
};

type PaymentStatusContainerProps = {
  paymentStatus: ReactNode;
  paymentTermsIndicator: ReactNode;
  price: number;
  paymentStatusIndicator: ReactNode;
  paymentMessage: ReactNode;
  onVatInvoiceClick?: () => void;
};
const PaymentStatusContainer = ({
  paymentStatus,
  paymentTermsIndicator,
  price,
  paymentStatusIndicator,
  paymentMessage,
  onVatInvoiceClick,
}: PaymentStatusContainerProps) => {
  return (
    <CardContent className={"min-h-[150px] p-4 md:col-span-1"}>
      <div className="flex h-full flex-col gap-4">
        {paymentStatus}
        <div className="flex flex-wrap gap-1">
          {paymentTermsIndicator}
          {price > 0 && paymentStatusIndicator}
        </div>
        {paymentMessage}
        {onVatInvoiceClick && (
          <div>
            <SmallButton>
              <Icon icon={"OPEN_IN_NEW"} />
              VAT INVOICE
            </SmallButton>
          </div>
        )}
      </div>
    </CardContent>
  );
};
